import Entity from "components/Entity";
import { WFA } from "./components/WorkFlowActionItem";
import { endpoints } from "config";
import * as Yup from "yup";
import { HomeFields } from "./models";
import { FieldRenderers, FormField } from "@avamae/formbuilder";
import { stringReadOnlyEditRenderer } from "components/helpers/CustomFieldRenderers/CustomString/StringReadOnlyEditFieldRenderer";
import { dateTimeV2Renderer } from "components/DateTimeFieldV2/DateTimeFieldV2";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useMemo, useRef, useState } from "react";
import { useFetch } from "api";
import _ from "lodash";
import { InitialDataResponse, getAxiosRequestConfig } from "components/Entity/Entity";
import { Spinner } from "components/Spinner/Spinner";
// import styles from "componens/Entity/Entity.module.scss";
import { PanelButtons } from "components/helpers/panelHelpers/PanelButtons";
import FormElements from "components/helpers/FormElements";
// import { WorkFlowProgress } from "./components/WorkFlowProgress";
import { MenuState } from "components/Category/CrudMenu";
import Button from "components/ui/Button";
import { useGetPermissions } from "helpers/useGetPermissions";
import { allPermissions } from "App";
import { stringV2Renderer } from "components/helpers/CustomFieldRenderers/CustomString/StringV2";
import { ApprovalOutcomeDecisionModal } from "./components/ApprovalOutcomeDecisionModal";
import {
  acceptedFileList,
  multiFileUploadFieldRenderer,
} from "components/helpers/FieldRenderers/FileUpload/multiFileUploadRenderer";
import { createMultipartSubmitForm, createSubmitForm } from "helpers/submitForm";
import { useDispatch } from "react-redux";
import { singleMultiSelectFieldRenderer } from "components/helpers/CustomFieldRenderers/SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import { textAreaFieldRenderer } from "components/helpers/CustomFieldRenderers/TextAreaFieldRenderer/TextAreaFieldRenderer";
import { FileInformation } from "components/helpers/CustomFieldRenderers/FileInformation/FileInformation";
import { WorkflowProgress } from "components/helpers/CustomFieldRenderers/WorkflowProgressFieldRenderer/WorkflowProgressFieldRenderer";
import { WorkFlowProgress } from "./components/WorkFlowProgress";
// import { WorkFlowProgress } from "./components/WorkFlowProgress";

type Props = {
  selectedData: WFA[];
  actionType: "edit" | "view";

  onDone(): void;
  idFromRowOption: React.ReactText | null;
  submitButtonText?: string;

  menuActions?: MenuState[1];
};
export const defaultRedirectAction = {
  //for start work flow panel
  permissionKey: "notifications-view",
  locationOrRouting: "home",
  menuType: "VIEW",
};

import { fileUploadField, requiredId, optionalString, createContextTest } from "helpers/validation";

const MAX_FILE_SIZE = 500000000;

const validationSchema_Action = Yup.object({
  files: fileUploadField({
    required: true,
    requiredMessage: "Please upload a file",
    acceptedTypes: acceptedFileList,
    maxSizeBytes: MAX_FILE_SIZE,
  }),
});

const validationSchema_Approval = Yup.object({
  approvalOutcomesID: requiredId("Please select an approval outcome"),
  reasonCodesID: Yup.number()
    .nullable()
    .test(
      createContextTest<number | null>(
        "reason-code-required",
        "Please provide a Reason code for the selected Approval outcome",
        function (item) {
          let valid = true;

          const context: any = this.options.context;

          try {
            if (
              context &&
              typeof context.approvalOutcomesID === "number" &&
              context.listApprovalOutcomes &&
              Array.isArray(context.listApprovalOutcomes) &&
              context.listApprovalOutcomes.length > 0
            ) {
              const match = context.listApprovalOutcomes.filter(
                (item: any) => item.value === context.approvalOutcomesID
              );

              if (
                match.length === 1 &&
                match[0].bReasonCode &&
                typeof context.reasonCodesID !== "number"
              ) {
                valid = false;
              }
            }
          } catch {
            console.log("Failed to validate reasonCodesID field");
          }

          return valid;
        }
      )
    ),

  comments: optionalString(),
});

const rightsRenderer = (input?: {
  data?: any;
  actions?: MenuState[1];
}): FieldRenderers<HomeFields> => ({
  textarea: textAreaFieldRenderer(false),
  datetime: dateTimeV2Renderer(false),
  stringreadonly: stringReadOnlyEditRenderer(null, null, false),
  integerreadonly: stringReadOnlyEditRenderer(null, null, true),
  string: stringV2Renderer(false, null, null, false),
  integer: stringV2Renderer(false, null, null, true),
  singleselect: singleMultiSelectFieldRenderer("singleselect"),
  multiselect: singleMultiSelectFieldRenderer("multiselect"),
  document: (_key, field) => <FileInformation field={field} />,
  workflowprogress: <T,>(_key: React.ReactText, field: FormField<T>) => {
    const permission = useGetPermissions(
      allPermissions,
      `/${defaultRedirectAction.locationOrRouting}`
    );
    const hasPermission = permission.get(defaultRedirectAction.permissionKey);

    return (
      <WorkFlowProgress
        label={field.label as string}
        fieldName={field.name as string}
        actions={hasPermission ? input?.actions : undefined}
      />
    );
  },

  file: multiFileUploadFieldRenderer({
    readOnly: false,
    fieldNames: {
      singleImageOrFile: {
        imageOrFile: "file",
        imageOrFileName: "fileName",
        imageOrFileDownloadUri: "fileDownloadUri",
        imageOrFileFieldName: "file",
      },
      multipleImagesOrFiles: "files",
    },
    isMulti: true,
    acceptedFileTypes: {},
    resetToClearAll: true,
  }),
});

const readOnlyRightsRenderer = (input?: {
  data?: any;
  actions?: MenuState[1];
}): FieldRenderers<HomeFields> => ({
  textarea: textAreaFieldRenderer(true),
  datetime: dateTimeV2Renderer(true),
  string: stringV2Renderer(true, null, null, false),
  integer: stringV2Renderer(true, null, null, true),
  file: multiFileUploadFieldRenderer({
    readOnly: false,
    fieldNames: {
      singleImageOrFile: {
        imageOrFile: "file",
        imageOrFileName: "fileName",
        imageOrFileDownloadUri: "fileDownloadUri",
        imageOrFileFieldName: "file",
      },
      multipleImagesOrFiles: "files",
    },
    isMulti: true,
    acceptedFileTypes: {},
    resetToClearAll: true,
  }),
  document: (_key, field) => <FileInformation field={field} />,

  workflowprogress: <T,>(_key: React.ReactText, field: FormField<T>) => {
    const permission = useGetPermissions(
      allPermissions,
      `/${defaultRedirectAction.locationOrRouting}`
    );
    const hasPermission = permission.get(defaultRedirectAction.permissionKey);

    return (
      <WorkflowProgress
        label={field.label as string}
        readOnly={true}
        fieldName={field.name as string}
        showViewWorkflow={true}
        showFormLabel={true}
        triggerCommentFieldName="communication"
      />
    );
  },
});

const fetchConfig = (selectedRows: WFA[], idFromRowOption?: React.ReactText | null) => {
  return {
    params: {
      Id:
        idFromRowOption ??
        (selectedRows[0]?.restrictedWorkflow__bWorkflowAction
          ? selectedRows[0]?.workflowActionInstances__Id
          : selectedRows[0]?.workflowApprovalInstances__Id),
      communicationsId: selectedRows[0]?.communications__Id
        ? selectedRows[0]?.communications__Id
        : null,
    },
  };
};

export const WorkFlowAction = (props: Props): JSX.Element => {
  const { selectedData, actionType, onDone, idFromRowOption, submitButtonText, menuActions } =
    props;
  // Define the action type based on the selected data
  const determinedActionType = selectedData[0]?.communications__Id
    ? "Notification"
    : selectedData[0]?.restrictedWorkflow__bWorkflowAction
      ? "Action"
      : "Approval";

  // Define the default endpoint based on the action type string
  const url =
    determinedActionType == "Notification"
      ? endpoints.communications.edit
      : determinedActionType == "Action"
        ? endpoints.home.reviewAction
        : endpoints.home.reviewApproval;

  // Endpoint for marking a communication notification as read
  const markAsReadUrl = endpoints.communications.markread;

  const cfg = useMemo(() => {
    return fetchConfig
      ? getAxiosRequestConfig(fetchConfig, selectedData, idFromRowOption)
      : undefined;
  }, [fetchConfig, selectedData, idFromRowOption]);

  const { data, loading, error } = useFetch<InitialDataResponse<HomeFields>>(url, cfg);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const formContainerRef = useRef<HTMLInputElement | null>(null);

  const formikRef = useRef<FormikProps<any>>(null) as any;

  const dispatch = useDispatch();
  const handleSubmit = (values: HomeFields, formikFelperActions: FormikHelpers<HomeFields>) => {
    formikFelperActions.setSubmitting(true);

    determinedActionType === "Notification"
      ? createSubmitForm(
          markAsReadUrl,
          () => {
            setModalOpen(false);
            onDone();
          },
          dispatch
        )({ ...values, bRead: true }, formikFelperActions)
      : determinedActionType === "Action"
        ? createMultipartSubmitForm(url, onDone, dispatch, [])(values, formikFelperActions)
        : createSubmitForm(
            url,
            () => {
              setModalOpen(false);
              onDone();
            },
            dispatch
          )(values, formikFelperActions);
  };

  const reviewGroupBy =
    determinedActionType === "Notification"
      ? reviewNotificationGroupBy
      : determinedActionType === "Action"
        ? reviewActionGroupBy
        : reviewApprovalGroupBy;

  if (loading && !data) {
    return <Spinner positionAbsolute />;
  }

  if (error) {
    throw error;
  }
  if (data) {
    const groupedMetaData = _.groupBy(data?.metadata, "groupLabel");

    return (
      <Formik
        initialValues={data.details}
        validationSchema={
          determinedActionType === "Notification"
            ? null
            : determinedActionType === "Action"
              ? validationSchema_Action
              : validationSchema_Approval
        }
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {(formik) => {
          return (
            <Form className={`ReviewWorkFlowActionOrApproval ${determinedActionType}`}>
              <div className="Content">
                <div className={`Form FieldsInBoxes`} ref={formContainerRef}>
                  <div
                    className={`CreateEditContainer TwoColumns ${
                      actionType ? `${actionType}` : ""
                    }`}
                  >
                    <div className={`Column1`}>
                      {reviewGroupBy.leftTop.groupLabel.map((l) => {
                        // if (l !== "Workflow Name") {}
                        return (
                          <div className={`Group Column1 ${l}`} key={`group-column-1-${l}`}>
                            <FormElements
                              values={formik.values}
                              data={{
                                ...data,
                                metadata: groupedMetaData[l],
                              }}
                              loadingFallback={<Spinner positionAbsolute />}
                              readOnly={reviewGroupBy.leftTop.readOnly}
                              fieldRenderers={
                                reviewGroupBy.rightTop.readOnly
                                  ? readOnlyRightsRenderer({
                                      data,
                                      actions: menuActions,
                                    })
                                  : rightsRenderer({
                                      data,
                                      actions: menuActions,
                                    })
                              }
                              hiddenFields={{ completionDate: true }}
                              key={`group-column-1-form-elements-${l}`}
                            />
                          </div>
                        );
                      })}

                      {reviewGroupBy.leftBottom.groupLabel.map((l) => {
                        return (
                          <div
                            className={`Group Column1 ${l}`}
                            key={`lower-half-group-column-1-${l}`}
                          >
                            <FormElements
                              values={formik.values}
                              data={{
                                ...data,
                                metadata: groupedMetaData[l],
                              }}
                              loadingFallback={<Spinner positionAbsolute />}
                              readOnly={reviewGroupBy.leftBottom.readOnly}
                              fieldRenderers={
                                reviewGroupBy.leftBottom.readOnly
                                  ? readOnlyRightsRenderer({
                                      data,
                                      actions: menuActions,
                                    })
                                  : rightsRenderer({
                                      data,
                                      actions: menuActions,
                                    })
                              }
                              key={`lower-half-group-column-1-form-elements-${l}`}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="Column2">
                      {reviewGroupBy.rightTop.groupLabel.map((l) => {
                        return (
                          <div className={`Group Column2 ${l}`} key={`group-column-2-${l}`}>
                            <FormElements
                              values={formik.values}
                              data={{
                                ...data,
                                metadata: groupedMetaData[l],
                              }}
                              loadingFallback={<Spinner positionAbsolute />}
                              readOnly={reviewGroupBy.rightTop.readOnly}
                              fieldRenderers={
                                reviewGroupBy.rightTop.readOnly
                                  ? readOnlyRightsRenderer({
                                      data,
                                      actions: menuActions,
                                    })
                                  : rightsRenderer({
                                      data,
                                      actions: menuActions,
                                    })
                              }
                              key={`group-column-2-form-elements-${l}`}
                            />
                          </div>
                        );
                      })}

                      {reviewGroupBy.rightBottom.groupLabel.map((l) => (
                        <div
                          className={`Group Column2 ${l}`}
                          key={`lower-half-group-column-2-${l}`}
                        >
                          <FormElements
                            values={formik.values}
                            data={{
                              ...data,
                              metadata: groupedMetaData[l],
                            }}
                            loadingFallback={<Spinner positionAbsolute />}
                            readOnly={reviewGroupBy.rightBottom.readOnly}
                            key={`lower-half-group-column-2-form-elements-${l}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="Buttons">
                  <div className="Btn BtnCancel" onClick={onDone}>
                    Cancel
                  </div>

                  <Button
                    type="submit"
                    disabled={formik.isSubmitting}
                    onClick={
                      determinedActionType === "Approval"
                        ? () => {
                            setModalOpen(true);
                          }
                        : undefined
                    }
                  >
                    {determinedActionType === "Notification" ? "Mark as read" : "Submit"}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  }
  return <div style={{ display: "block" }}></div>;
};

const reviewActionGroupBy = {
  leftTop: {
    groupLabel: ["Details", "Workflow Message"],

    readOnly: true,
  },
  leftBottom: {
    groupLabel: ["Document", "Comments"],
    readOnly: false,
  },
  rightBottom: { groupLabel: [], readOnly: false },
  rightTop: {
    groupLabel: ["Workflow Progress"],
    readOnly: true,
  },
};
const reviewApprovalGroupBy = {
  leftTop: {
    groupLabel: ["Details", "Workflow Message", "Document"],
    readOnly: true,
  },
  leftBottom: {
    groupLabel: ["Approval outcome", "Reason code", "Comments"],
    readOnly: false,
  },
  rightBottom: {
    groupLabel: [],
    readOnly: false,
  },
  rightTop: { groupLabel: ["Workflow Progress"], readOnly: true },
};

const reviewNotificationGroupBy = {
  leftTop: {
    groupLabel: ["Details", "Trigger Communication"],
    readOnly: true,
  },
  leftBottom: { groupLabel: [], readOnly: false },
  rightBottom: { groupLabel: [], readOnly: false },
  rightTop: {
    groupLabel: ["Workflow Progress"],
    readOnly: true,
  },
};
